import { TASK_TYPES } from '@claimup/shared/constants/assignments';

const REPLY_TASK_TYPES = TASK_TYPES.filter(type => type.startsWith('reply'));

/**
 * Build the querystring used to fetch expedients with assignments,
 * applying all active URL filters. Centralized so socket handlers and
 * the list fetch hook produce the same URL and stay in sync.
 *
 * Recognized URL params:
 * - filter: 'all' | 'reply' | 'mine'
 * - task_type: repeatable, narrows by assignment task_type
 * - assigned_to: single user id
 * - client_id: single user id (filters expedients by user_id)
 * - estado: single expedient status (filters expedients by estado)
 *
 * @param {URLSearchParams} searchParams
 * @param {{ page?: number, limit?: number, expedientId?: string, currentUserId?: string }} options
 * @returns {string}
 */
export const buildFilterQueryParams = (searchParams, options = {}) => {
  const { page, limit, expedientId, currentUserId } = options;
  const filter = searchParams.get('filter') || 'all';
  const params = new URLSearchParams();

  params.set('status', 'pending');
  if (page) params.set('page', String(page));
  if (limit) params.set('limit', String(limit));
  if (expedientId) params.set('expedient_id', String(expedientId));

  if (filter === 'reply') {
    REPLY_TASK_TYPES.forEach(type => params.append('task_type', type));
  } else if (filter === 'mine' && currentUserId) {
    params.set('assigned_to', String(currentUserId));
  }

  // Additional task_type filters from the URL stack on top of the tab filter.
  searchParams.getAll('task_type').forEach(type => params.append('task_type', type));

  const assignedTo = searchParams.get('assigned_to');
  // The 'mine' tab already sets assigned_to from currentUserId; explicit URL
  // value still wins when present (e.g., user is on 'all' and picks an admin).
  if (assignedTo) params.set('assigned_to', assignedTo);

  const clientId = searchParams.get('client_id');
  if (clientId) params.set('client_id', clientId);

  const estado = searchParams.get('estado');
  if (estado) params.set('estado', estado);

  return params.toString();
};

/**
 * Whether a given assignment matches the active URL filter, so socket
 * handlers can short-circuit before hitting the network.
 *
 * @param {URLSearchParams} searchParams
 * @param {{ task_type?: string, assigned_to?: string, expedient_id?: string }} assignment
 * @param {{ currentUserId?: string }} [options]
 */
export const assignmentMatchesActiveFilter = (searchParams, assignment, options = {}) => {
  const filter = searchParams.get('filter') || 'all';

  if (filter === 'reply' && !REPLY_TASK_TYPES.includes(assignment?.task_type)) return false;
  if (filter === 'mine') {
    const me = options.currentUserId;
    if (!me || String(assignment?.assigned_to) !== String(me)) return false;
  }

  const taskTypes = searchParams.getAll('task_type');
  if (taskTypes.length > 0 && !taskTypes.includes(assignment?.task_type)) return false;

  const assignedTo = searchParams.get('assigned_to');
  if (assignedTo && String(assignment?.assigned_to) !== String(assignedTo)) return false;

  // client_id can't be checked from the assignment alone (needs expedient.user_id);
  // assume true here and let the server-side fetch reject if it doesn't match.

  return true;
};
